import { Brand, Vehicle } from "../../types";
import VehicleCard from "../Home/components/VehicleCard/VehicleCard";
import DataTablePagination from "../../components/DataTable/Pagination";
import { Head, router } from "@inertiajs/react";
import { Search } from "lucide-react";
import "./styles.css";
import Filters, { FilterProps } from "./components/Filters/Filters";
import { Input } from "../../components/ui";
import { useEffect, useRef } from "react";
import { debounce } from "lodash";
import { buildSearchUrl } from "../../utils/buildSearchUrl";

type VehicleIndexProps = {
  vehicles: Vehicle[];
  filters: Array<FilterProps>;
  brands: Brand[];
  modeles: string[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_items: number;
    per_page: number;
  };
  search: {
    q: string;
  };
};

type VehicleSearchFormProps = {
  onSearch: (search?: string) => void;
  value: string;
};

const VehicleSearchForm: React.FC<VehicleSearchFormProps> = (props) => {
  const { value, onSearch } = props;

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  const debouncedSearch = debounce((v: string) => {
    v.length > 0 ? onSearch(v) : onSearch(undefined);
  }, 500);

  const onKeyDown = (e: any) => {
    if (e.key === "Enter") {
      debouncedSearch.cancel();
      e.target && e.target.value.length > 0
        ? onSearch(e.target.value)
        : onSearch(undefined);
    }
  };

  const onClick = (e: any) => {
    const v = inputRef.current?.value;
    debouncedSearch.cancel();
    v && v.length > 0 ? onSearch(v) : onSearch(undefined);
    e.preventDefault();
  };

  return (
    <div className="search-input-wrapper">
      <Input
        ref={inputRef}
        defaultValue={value || ""}
        onKeyDown={onKeyDown}
        onChange={(e) => debouncedSearch(e.target.value)}
        type="text"
        placeholder={"Rechercher"}
        className="search-input"
      />
      <Search className="search-input-icon" onClick={onClick} />
    </div>
  );
};

const VehicleIndex = (props: VehicleIndexProps) => {
  const { vehicles, pagination, search = {}, brands, modeles } = props;

  const onPaginationChange = (page: number, perPage: number) => {
    router.get(
      "/vehicles",
      {
        page,
        per_page: perPage,
        search: search,
      },
      { preserveState: true, preserveScroll: true },
    );
  };

  const getPageHref = (page: number) =>
    buildSearchUrl(
      "/vehicles",
      { page, per_page: pagination.per_page },
      search,
    );

  const onSearch = (value: string) => {
    router.get(
      "/vehicles",
      {
        search: {
          ...search,
          q: value,
          page: 1,
          perPage: pagination.per_page,
        },
      },
      { preserveState: true, preserveScroll: true },
    );
  };

  const onFilterChange = (filter: string, value: string) => {
    const nextSearch = {
      ...search,
    };
    if (value) nextSearch[filter] = value;
    else delete nextSearch[filter];

    if (filter === "brand") nextSearch.model = "all";
    router.get(
      "/vehicles",
      { search: nextSearch },
      { preserveState: true, preserveScroll: true },
    );
  };

  const onReset = () => {
    router.get("/vehicles", { preserveState: true, preserveScroll: true });
  };
  console.log(search);

  return (
    <div className="my-4 lg:my-10 px-4 container mx-auto">
      <Head title="Liste des véhicules - Review car" />
      <h1 className="text-center lg:max-w-[75%] mx-auto">
        Liste de nos
        <span className="primary-color"> véhicules</span>
      </h1>
      <>
        <div className="filters-container lg:mt-4 col-span-12 md:col-span-3">
          <Filters
            values={search}
            brands={brands}
            modeles={modeles}
            onFilterChange={onFilterChange}
            onReset={onReset}
          >
            <VehicleSearchForm onSearch={onSearch} value={search?.q} />
          </Filters>
        </div>
        {vehicles.length > 0 ? (
          <>
            <div className="flex flex-col items-center">
              <div className="w-full">
                <div className="vehicle-container">
                  {vehicles.map((vehicle: Vehicle) => (
                    <VehicleCard key={vehicle.id} vehicle={vehicle} />
                  ))}
                </div>
                <>
                  {pagination && pagination.total_pages > 1 && (
                    <DataTablePagination
                      onChange={onPaginationChange}
                      current={pagination.current_page}
                      total={pagination.total_pages}
                      perPage={pagination.per_page}
                      showPageSizeOptions={false}
                      pageSizeOptions={[18, 30, 50, 100]}
                      getPageHref={getPageHref}
                    />
                  )}
                </>
              </div>
            </div>
          </>
        ) : (
          <span className="text-muted-foreground">Aucun véhicule trouvé</span>
        )}
      </>
    </div>
  );
};

export default VehicleIndex;
